// VRG-712 — cells shared by Visitor history and the journey drawer: one way to show a
// visitor's country and the tracked link they came through (§36).
import { Box, Stack, Typography } from '@mui/material'

import { EMPTY_VALUE } from '@repo/ui'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { getFlagUrl } from 'components/input/fields/SelectCountryField'
import type { WebsiteTrackedLinkRef } from 'services'

import type { TrackedLinkModalTarget } from '../../TrackedLinks/TrackedLinkModal'

export const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

export const Muted = ({ children, title }: { children?: React.ReactNode; title?: string }) => (
  <Box sx={cellSx} title={title}>
    <Typography variant="body2" color={children ? 'text.primary' : 'text.disabled'} noWrap>
      {children || EMPTY_VALUE}
    </Typography>
  </Box>
)

/** Flag + country name, city underneath. Shared with the journey drawer. */
export const CountryLabel = ({
  code,
  city,
  names,
}: {
  code: string | null
  city?: string | null
  names: ReadonlyMap<string, string>
}) => {
  if (!code) return <Muted />
  const name = names.get(code.toUpperCase()) ?? code.toUpperCase()
  const flag = getFlagUrl(code)
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
      {flag ? <Box component="img" src={flag} alt="" sx={{ width: 20, height: 14, borderRadius: '2px', flexShrink: 0, objectFit: 'cover' }} /> : null}
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap title={name}>
          {name}
        </Typography>
        {city ? (
          <Typography variant="body3" color="text.secondary" noWrap>
            {city}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  )
}

/** The tracked link a visit came through: its label (opens the link's window), else the raw code. */
export const TrackedLinkLabel = ({
  link,
  code,
  onOpen,
}: {
  link: WebsiteTrackedLinkRef | null
  code: string | null
  onOpen: (target: TrackedLinkModalTarget) => void
}) => {
  if (link)
    return (
      <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
        <Typography
          component="button"
          type="button"
          variant="body2"
          noWrap
          title={`${link.label}: open the tracked link`}
          onClick={(e) => {
            e.stopPropagation()
            onOpen({ id: link.id, label: link.label, description: `vera.inc/invite/${link.code}` })
          }}
          data-testid="visitor-tracked-link"
          sx={{ p: 0, border: 0, bgcolor: 'transparent', color: 'text.primary', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', '&:hover': { textDecoration: 'underline' } }}
        >
          {link.label}
        </Typography>
        <Typography variant="body3" color="text.secondary" noWrap>
          {link.archived ? 'Archived link' : `/invite/${link.code}`}
        </Typography>
      </Stack>
    )
  if (code)
    return (
      <InfoTooltip text="This visit carries a tracked-link code that matches no tracked link in vera-2 (made elsewhere, or mistyped).">
        <Box sx={cellSx} tabIndex={0}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {code}
          </Typography>
        </Box>
      </InfoTooltip>
    )
  return <Muted />
}

